import { createGlobalStyle } from 'styled-components';
import {
  CONCIERGE_BASE_SCHEME_CSS,
  CONCIERGE_LIGHT_MAX_CONTRAST_SCHEME_CSS,
  CONCIERGE_LIGHT_SCHEME_CSS,
  CONCIERGE_PRIMITIVES_CSS,
} from '@dutchie/common/utils/concierge-theme-css';

/**
 * Concierge Mode token layer — two tiers.
 *
 *   TIER 1: primitives (gold/forest/cream ramps) on `:root`.
 *   TIER 2a: concierge aliases on `:root` (AI accent family — the
 *            single tenant override hook).
 *   TIER 2b: marketplace `--dcm-*` overrides under `.concierge-mode`
 *            so existing components re-skin with no per-component edits;
 *            light variant nests under `[data-concierge-theme="light"]`,
 *            mid-tone escalation under `[data-concierge-theme="light-max-contrast"]`.
 *
 * The declaration blocks live in `@dutchie/common/utils/concierge-theme-css` so the
 * Admin live preview interpolates the exact same strings under its own
 * selectors — preview and production cannot drift.
 *
 * Escape hatch: when a component's non-Concierge value isn't any existing
 * `--dcm-*` token's non-Concierge value (e.g. a fixed design literal),
 * declare a local var on the component and override it with a scoped
 * `.concierge-mode &` block there instead of here. See the `--otm-*` vars
 * in order-type-takeover-modal.tsx.
 *
 * Override `--dcm-concierge-accent` on `.concierge-mode` to swap gold for
 * a custom brand color — propagates through hover/soft/glow (color-mix
 * variants), `--dcm-color-primary`, the effect-chip family, and the
 * strain-dot triad. Layout values (padding/radius/etc.) live in components,
 * not here. `--dcm-color-status-*` is NOT overridden (compliance Status
 * Lock-Out Rule).
 *
 * Accent and canvas are also driven per-tenant by
 * `webCustomizationSettingsV2.concierge` via `buildConciergeOverridesCss`
 * (see `concierge-theme.ts`) — that pre-validated CSS is passed in through
 * the `$tenantOverrides` prop and interpolated last, below, so it wins the
 * cascade over the TIER 2b base blocks.
 *
 * Cascade:
 *   :root                                                        marketplace + tenant + (this) TIER 1/2a
 *   .concierge-mode                                              TIER 2b base (dark, no attribute — base layer)
 *   .concierge-mode[data-concierge-theme="light"]                TIER 2b light deltas
 *   .concierge-mode[data-concierge-theme="light-max-contrast"]   TIER 2b light + escalated ink
 *   .concierge-mode / [data-concierge-theme="light"|"light-max-contrast"] tenant overrides ($tenantOverrides, last)
 */
export const ConciergeVariables = createGlobalStyle<{ $tenantOverrides?: string }>`
  :root {
${CONCIERGE_PRIMITIVES_CSS}
  }

  /* ==================================================================
     TIER 2b: MARKETPLACE ALIAS OVERRIDES — BASE (DARK)

     Applied via the .concierge-mode wrapper. Redefines select --dcm-*
     tokens so existing marketplace components re-skin to the
     Concierge dark canvas with the gold primary, without any
     component-level changes.
     ================================================================== */
  .concierge-mode {
${CONCIERGE_BASE_SCHEME_CSS}

    /* Loyalty (assets/loyalty.tsx) is rendered from several call sites with
       no shared styled-component ancestor, so its escape-hatch var is
       declared here at .concierge-mode scope (inherits to every instance)
       instead of on a per-usage local wrapper. The default/light value is
       supplied via the var() fallback at the consuming <circle>, not here. */
    --loyalty-badge-bg: var(--dcm-color-surface-muted);
  }

  /* ==================================================================
     TIER 2b: MARKETPLACE ALIAS OVERRIDES — LIGHT VARIANT

     Opt-in via [data-concierge-theme="light"] on the wrapper.
     Cream canvas, marketplace inks, same gold primary.
     ================================================================== */
  .concierge-mode[data-concierge-theme="light"] {
${CONCIERGE_LIGHT_SCHEME_CSS}
  }

  /* ==================================================================
     TIER 2b: MARKETPLACE ALIAS OVERRIDES — LIGHT, MAX CONTRAST

     Opt-in via [data-concierge-theme="light-max-contrast"]. A mid-tone
     canvas that no brand ink renders at 4.5:1: light surfaces, deep
     forest ink. Same specificity as the light block; source order
     decides, and only one of the two attribute values is ever set.
     ================================================================== */
  .concierge-mode[data-concierge-theme="light-max-contrast"] {
${CONCIERGE_LIGHT_MAX_CONTRAST_SCHEME_CSS}
  }

  /* Tenant overrides (Admin-driven) — MUST stay last so they win the
     cascade over the base blocks above. Content is pre-validated CSS from
     buildConciergeOverridesCss. */
  ${({ $tenantOverrides }) => $tenantOverrides ?? ''}
`;
